import React from 'react';
import Select from 'react-select';
import { Search } from 'lib/components';
import styled, { useTheme } from 'styled-components/macro';
import { secondarySelectStyle } from 'lib/style/commonStyles';
import { createVDPIntegrationOptions } from 'lib/utils/vdp';
import { useAuth } from 'lib/context';
import { AuthorInfo, VdpParams, VehicleInfo } from 'lib/api/vdp/types';
import { useGetUsersWithVdp } from 'lib/api/vdp/useGetUsersWithVdp';
import { useGetUniqueVins } from 'lib/api/vdp/useGetUniqueVins';
import { getDisplayName } from 'lib/utils/functions';
import { useTranslation } from 'react-i18next';

const FiltersContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 24px;
`;

const Filter = styled.div<{ minWidth: string }>`
  margin-right: 12px;
  min-width: ${({ minWidth }) => minWidth};
`;

const CustomOptionLabel = styled.div`
  display: flex;
  align-items: center;
`;

const Vin = styled.div`
  margin-right: auto;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: ${({ theme }) => theme.colors.neutral[100]};
`;

const Title = styled.div`
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: ${({ theme }) => theme.colors.neutral[60]};
  width: 230px;
  text-align: right;
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const vinSelectStyle = (themes: any) => ({
  control: (styles: any, state: any) => ({
    ...styles,
    height: '40px',
    boxShadow: 'none',
    borderColor: state.isFocused
      ? themes.colors.neutral[100]
      : themes.colors.neutral[40],
    ':hover': {
      borderColor: themes.colors.neutral[100],
    },
    ':focus': {
      borderColor: themes.colors.neutral[100],
    },
    ':active': {
      borderColor: themes.colors.neutral[100],
    },

    cursor: 'pointer',
  }),
  option: (base: any) => ({
    ...base,
    backgroundColor: 'transparent',
    color: themes.colors.neutral[100],
    padding: '8px',
    cursor: 'pointer',
    ':hover': {
      backgroundColor: 'transparent',
      opacity: '.8',
    },
    ':focus': {
      backgroundColor: 'transparent',
      opacity: '.8',
    },
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  menu: (base: any) => ({
    ...base,
    minWidth: 448,
  }),
});

type OptionLabelProps = {
  value: string;
  label: string;
  title?: string;
  valueLabel?: string;
};

const formatOptionLabel = ({
  valueLabel = '',
  title = '',
}: OptionLabelProps) => (
  <CustomOptionLabel>
    <Vin>{valueLabel}</Vin>
    <Title>{title}</Title>
  </CustomOptionLabel>
);

type Props = {
  params: VdpParams;
  setParams: React.Dispatch<React.SetStateAction<VdpParams>>;
};

export const VdpFilters = ({ params, setParams }: Props) => {
  const { userData } = useAuth();
  const { t } = useTranslation();
  const themes = useTheme();
  const { data: vdpAuthors } = useGetUsersWithVdp();
  const { data: vdpVins } = useGetUniqueVins();

  const authorOptions: OptionLabelProps[] = [
    {
      value: '0',
      label: t('All Users'),
    },
    ...(vdpAuthors || []).map((author: AuthorInfo) => ({
      label: getDisplayName([author.firstName, author.lastName]),
      value: author.userId.toString(),
    })),
  ];

  const vinOptions = [
    { value: '', label: t('All VINs'), valueLabel: t('All VINs') },
    ...(vdpVins || []).map((vehicle: VehicleInfo) => {
      const vehicleTitle = getDisplayName([
        vehicle.make,
        vehicle.model,
        vehicle.year,
      ]);
      return {
        value: vehicle.vin,
        label: `${vehicle.vin} ${vehicleTitle}`,
        title: vehicleTitle,
        valueLabel: vehicle.vin,
      };
    }),
  ];

  const integrationTypeOptions = createVDPIntegrationOptions(userData);

  return (
    <FiltersContainer>
      <Filter minWidth='342px'>
        <Search
          placeholder={t('Search videos by name or VIN…')}
          onSearch={(term: string) =>
            setParams(prev => ({ ...prev, search: term, start: 0 }))
          }
          width={'342px'}
        />
      </Filter>
      <Filter minWidth='230px'>
        <Select
          options={authorOptions}
          value={authorOptions.find(
            option => option?.value === params.userId?.toString()
          )}
          onChange={option => {
            setParams(prev => ({
              ...prev,
              userId: Number(option?.value) || 0,
              start: 0,
            }));
          }}
          styles={secondarySelectStyle}
        />
      </Filter>
      <Filter minWidth='260px'>
        <Select
          options={vinOptions}
          value={vinOptions.find(option => option?.value === params.vin)}
          onChange={option =>
            setParams(prev => ({ ...prev, vin: option?.value || '', start: 0 }))
          }
          styles={vinSelectStyle(themes)}
          formatOptionLabel={formatOptionLabel}
        />
      </Filter>

      <Filter minWidth='140px'>
        <Select
          options={integrationTypeOptions}
          styles={secondarySelectStyle}
          value={integrationTypeOptions.find(
            option => option?.value === params.integrationType
          )}
          onChange={option => {
            setParams(prev => ({
              ...prev,
              integrationType: option?.value || '',
              start: 0,
            }));
          }}
        />
      </Filter>
    </FiltersContainer>
  );
};
